import { Driver } from './driver.model';
import { Passenger } from './passenger.model';
import { Provider } from './provider.model';
import { TripLegStatusBC } from './trip-leg-status.model';
import { TripLegExceptionLogEntry } from './trip-leg.model';

export interface ReservationPhone {
  phoneNumberTypeBC: string;
  passengerPhoneUseBC: string;
  phoneNumber: string;
  phoneExtension?: string;
  hasSMSConsent: boolean;
}

export interface Vehicle {
  vehicleID: number;
  transportationProviderID: number;
  licensePlate: string;
  licensePlateIssuedInStateCode: string;
  registrationExpiresOn: string;
  vehicleVIN: string;
  vehicleTypeID: number;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  vehicleColor: string;
  isClearToTransport: boolean;
  isInternal: boolean;
  vehicleState: string;
  createdByUserID: number;
  createdOnUTC: string;
  modifiedByUserID: number;
  modifiedOnUTC: string;
}

export enum TripLegType {
  OneWay = 1,
  RoundTrip = 2,
  MultiLeg = 3,
}

export interface TripLegAddress {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  stateCode: string;
  zipCode: string;
  county?: string;
  latitude?: number;
  longitude?: number;
  timeZone?: string;
  placeID?: string;
}

export interface TripLegAlternateContact {
  alternateContactUUID?: string;
  alternateContactTypeBC: string;
  hasSMSConsent: boolean;
  firstName: string;
  lastName: string;
  locationName?: string;
  phoneNumberTypeBC: string | null;
  phoneNumber: string | null;
  phoneExtension?: string;
}

export interface TripLegStatusLogEntry {
  tripLegStatusLogUUID: string;
  tripLegStatusID: string;
  tripLegStatusBC: string;
  createdOnUTC: string;
  createdByUserID: number;
  userName?: string; // used internal by app - not returned by the server
}

export interface TripLegOverride {
  tripLegOverrideReasonBC: string;
  clientFirstName: string;
  clientLastName: string;
  tripLegOverrideUUID?: string;
  requestingUserID?: string;
  requestedDateTimeUTC?: string;
  approvingUserID?: string;
  approvalDateTimeUTC?: string;
  servicePlanUUID?: string;
  isApproved?: boolean;
}

export interface TriplegMetadata {
  roundTripReturnLeg?: boolean;
  roundTrip: boolean;
}

export interface ServicePlan {
  servicePlanUUID: string;
  rejectedReasons: string[];
  eligibleTransportationServiceUUIDs: string[];
}

export interface TripLegProviderCallOutcome {
  tripLegProviderCallOutcomeID: string;
  tripLegID: number;
  transportationProviderID?: number;
  callOutcomeID: number;
  callOutcomeRejectReasonID?: number;
  rejectedReason?: string;
  serviceAssurance?: string;
  status?: string;
  statusETA?: string;
  createdOnUTC?: string;
  createdByUserID?: string;
  modifiedOnUTC?: string;
  modifiedByUserID?: string;
}

export enum StandingOrderReturnType {
  willCall = 'WILLCALL',
  time = 'TIME',
}

export interface TripLeg {
  passengerUUID: string;
  passengerFirstName?: string;
  passengerMiddleName?: string;
  passengerLastName?: string;
  transportationProviderName?: string;
  driverFirstName?: string;
  driverLastName?: string;
  passenger?: Passenger;
  driverID?: number;
  reservationReferenceID?: string;
  clientBC?: string;
  tripLegID?: number;
  tripLegReferenceID?: string;
  tripLegSequence: number;
  purposeCodeUUID: string;
  appointmentDate?: string;
  appointmentDateTime?: string;
  appointmentTimeUTC?: string;
  pickUpDueDateTime?: string;
  pickUpDueTimeUTC?: string;
  pickUpAddressTimeZoneIANA?: string;
  estimatedDistance?: number;
  estimatedDistanceMiles?: string;
  invoiceDistanceMiles?: string;
  payableAmount?: string;
  isWillCall: boolean;
  companionCount: number;
  overrides: TripLegOverride[];
  gurney?: GurneyInfo;
  pickUpAddress: TripLegAddress;
  pickUpNote?: string;
  dropOffAddress: TripLegAddress;
  dropOffNote?: string;
  pickUpPhone?: ReservationPhone;
  dropOffPhone?: ReservationPhone;
  alternateContact?: TripLegAlternateContact;
  transportationAttributes: string[][];
  tripLegPassengerNoteTypeBCs: string[];
  tripLegDriverNoteTypeBCs: string[];
  metadata: TriplegMetadata;
  servicePlans?: ServicePlan[]; // this needs to be updated to be real service plan info
  tripLegProviderCallOutcomes?: TripLegProviderCallOutcome[];
  transportationProviders?: {
    transportationProviderID: number;
    providerTransportationServiceUUID: string;
    clientTransportationServiceUUID: string;
    coverageAreaID: number;
    tripLegValue: number;
    isSelected: boolean;
  }[];
  transportationProvider?: Provider;
  transportationProviderID?: number;
  transportationProviderTierID?: number;
  tripLeg?: SyncedTripLeg;
  passengerCareLevelBC?: string;
  passengerCareLevelName?: string;
  driver?: Driver;
  tripModeBC?: string;
  tripModeName?: string;
  isUnderDiscussion?: boolean;
  isEscalated?: boolean;
  discussionReason?: string;

  // these are never set on the client but sometimes displayed or used for other purposes
  state?: string;
  status?: string;

  // the following are part of a full trip leg model but not used by this client
  serviceSegmentUUID?: string;
  tripDenialReasonID?: number | null;
  offeredMiles?: number;
  actualMiles?: number;
  billableMiles?: number;
  waitMinutes?: number;
  tripLegStatusLogs?: TripLegStatusLogEntry[];

  // these are used just during edit time to track if the leg is part of a two leg round trip
  isRoundTrip?: boolean;
  roundTripWillCall?: boolean;
  roundTripReturnTime?: string;
  roundTripReturnTimeZone?: string;
  roundTripTripLegReferenceID?: string;

  tripLegRejectedReasons?: string;
  tripLegRejectedReasonBC?: string;
  tripLegStatusBC?: string;
  tripLegStatusName?: string;

  requestedTransportationProviderID?: number;

  isException?: boolean;
  tripLegExceptionBC?: string | null;
  tripLegExceptionLogs?: TripLegExceptionLogEntry[];
  vehicle?: Vehicle | null;

  isInternal?: boolean;
}

export interface GurneyInfo {
  gurneyTypeTransportationAttributeUUID?: string;
  memberHeightInches?: number;
  memberWeightLbs?: number;
  oxygenCapacityTransportationAttributeUUID?: string | null;
  stairsCountTransportationAttributeUUID?: string | null;
  waitAndReturn?: boolean;
}

export interface SyncedTripLeg {
  reservationID: number;
  tripLegID: number;
  tripLegReferenceID: string;
  tripLegSequence: number;
  appointmentTimeUTC: string;
  appointmentTimeZoneID: 2;
  pickUpDueTimeUTC: string;
  pickUpAddressTimeZoneID: number;
  passengerFirstName?: string;
  passengerMiddleName?: string;
  passengerLastName?: string;
  passengerCareLevelID: number;
  tripModeID: number;
  servicePlanID: number;
  serviceSegmentID: null;
  purposeCodeID: number;
  transportationProviderID: null;
  driverID: number | null;
  vehicleID: number | null;
  isWillCall: boolean;
  estimatedDistanceMiles?: string;
  invoiceDistanceMiles?: string;
  payableAmount?: string;
  companionCount: number;
  pickUpAddressLine1: string;
  pickUpAddressLine2: string;
  pickUpCity: string;
  pickUpStateCode: string;
  pickUpZipCode: string;
  pickUpCounty: string;
  pickUpAddressLatitude: string;
  pickUpAddressLongitude: string;
  dropOffAddressLine1: string;
  dropOffAddressLine2: string;
  dropOffCity: string;
  dropOffStateCode: string;
  dropOffZipCode: string;
  dropOffCounty: string;
  dropOffAddressLatitude: string;
  dropOffAddressLongitude: string;
  tripLegState: string;
  appointmentDate: string;
  transportationTypeID: number | null;
  dropOffAddressTimeZoneID: number;
  pickUpPassengerPhoneNumber: string;
  pickUpPassengerPhoneExtension: string;
  dropOffPassengerPhoneNumber: string;
  dropOffPassengerPhoneExtension: string;
  driver: Driver | null;
  tripLegDriverLocations?: TripLegDriverLocation[];
  transportationProvider: any | null;
  tripLegProviderCallOutcomes?: TripLegProviderCallOutcome[];
  reservationReferenceID: string;
  passengerUUID?: string;
  passenger?: Passenger;
  clientBC?: string;
  state?: string;
  status?: string;
  vehicle?: Vehicle | null;
  isEscalated?: boolean;
  isInternal?: boolean;
  reservation?: Reservation;
}

export interface TripLegDriverLocation {
  driverID: number;
  latitude: string;
  longitude: string;
  tripLegDriverLocationID: string;
  tripLegID: number;
  estimatedTimeOfArrivalSeconds: number;
}

export enum DayOfWeek {
  monday = 'MONDAY',
  tuesday = 'TUESDAY',
  wednesday = 'WEDNESDAY',
  thursday = 'THURSDAY',
  friday = 'FRIDAY',
  saturday = 'SATURDAY',
  sunday = 'SUNDAY',
}

export interface StandingOrder {
  effectiveStartDate: string;
  effectiveEndDate: string;
  schedule: {
    dayOfWeek: DayOfWeek;
    times: {
      tripLegSequence: number;
      appointmentTime: string;
      pickUpTime: string;
    }[];
  }[];
}

export interface Reservation {
  reservationServiceDate?: string;
  reservationReferenceID?: string;
  clientBC: string;
  passengerUUID: string;
  tripLegs: TripLeg[];
  state?: string;
  standingOrderUUID?: string;

  // the following are part of a full reservation model but not used by this client
  preferredTransportationProviderUUID?: string | null;
  isVIP?: boolean;
  isDeleted?: boolean;
  isInternal?: boolean;
}

export interface ReservationCreate {
  reservationReferenceID?: string;
  clientBC: string;
  passengerUUID: string;
}

export interface ReservationValidation {
  fieldName: string;
  tripLegSequence?: number;
  messageId: string;
  message: string;
}

export interface TripLegUpdate {
  tripLegSequence: number;
  pickUpAddress: TripLegAddress;
  dropOffAddress: TripLegAddress;
  estimatedDistance: number;
  servicePlans: ServicePlan[];
}

export interface ReservationValidations {
  tripLegUpdates: TripLegUpdate[];
  errors: ReservationValidation[];
  warnings: ReservationValidation[];
  log: string[];
  logContextID?: string;
}

export enum OnHoldTripLegApproveDenyAction {
  Approve = 'approve',
  Deny = 'deny',
}

export enum ReservationStatus {
  OnHold = 'onhold',
  // TODO: fill all statuses
}

export interface TripLegUpdate {
  passengerUUID: string;
  tripLegReferenceID: string;
  action: string;
}

export interface Reservations {
  clientBC: string;
  passengerUUID: string;
  tripLegs: TripLeg[];
  modifiedByUserID: string;
  modifiedDateTimeUTC: string;
  correlationUUID: string;
  reservationReferenceID: string;
  reservationServiceDate: string;
  state: string;
}

export enum ReservationModelState {
  active = 'active',
  inactive = 'inactive',
  draft = 'draft',
}

export interface EstimatedPickupTime {
  baseBuffer: number;
  travelTimeFactor: number;
  distance: number;
  totalPickupTime: number;
}

// NOTE: this interface is very badly named. It has nothing to do with TripLegPackages.
// It's not clear what this is for. The top level looks like a lot of information about
// an individual trip leg but it also has a tripLegs[] attribute.
export interface TripLegPackage {
  transportationProviderID: number;
  tripLegReferenceID: string;
  packageUUID: string;
  status: string;
  state: string;
  appointmentDate: string;
  pickUpDueTimeUTC: string;
  pickUpAddressTimeZoneIANA: string;
  tripModeBC: string;
  tripModeName: string;
  passengerCareLevelBC: string;
  passengerCareLevelName: string;
  tripLeg?: TripLeg;
  tripLegID?: number;
  manualDriverName?: string;
  driver?: Driver;
  tripLegs: TripLeg[];
  tripLegStatusLogs?: TripLegStatusLogEntry[];
  tripLegDriverLocations?: TripLegDriverLocation[];
  passenger?: Passenger;
  tripLegStatusBC: TripLegStatusBC;
  isUnderDiscussion?: boolean;
  isException?: boolean;
  tripLegExceptionID?: number;
  tripLegExceptionBC?: TripLegExceptionBC;
  isWillCall?: boolean;
  pickUpAddressLine1?: string;
  pickUpAddressLine2?: string;
  pickUpCity?: string;
  pickUpStateCode?: string;
  pickUpZipCode?: string;
  dropOffAddressLine1?: string;
  dropOffAddressLine2?: string;
  dropOffCity?: string;
  dropOffStateCode?: string;
  dropOffZipCode?: string;
  invoiceDistanceMiles?: string;
  payableAmount?: string;
  tripLegAddOnLineItems?: TripLegAddOnLineItem[];
}

enum TripLegExceptionBC {
  ReadyInvoice = 'READYINVOICE',
  NoDriver = 'NODRIVER',
  DriverNotOnline = 'DRIVERNOTONLINE',
  DriverLate = 'DRIVERLATE',
}
export interface TripLegAddOnLineItem {
  tripLegAddOnLineItemID: number;
  lineItemQuantity: number;
  lineItemAmount: string;
  lineItemDescription: string;
  tripLegID: number;
  tripLegAdjustmentTypeID: number;
  createdByUserID: number;
  createdOnUTC: string;
  modifiedByUserID: number;
  modifiedOnUTC: string;
}
